import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Tooltip } from "primereact/tooltip";
import styled from "styled-components";

export const SUGGESTION_ITEM_HEIGHT = 52;

export const StyledAutoComplete = styled(AutoComplete)`
  width: 100%;

  .p-autocomplete-multiple-container {
    position: relative;
    width: 100%;
    padding: 0.6rem 1rem 0.6rem 2.75rem;
    border-radius: 16px;
    background: var(--brand-white);
    border: 1px solid
      color-mix(in srgb, var(--brand-purple-500) 80%, var(--brand-white));
  }

  .p-autocomplete-multiple-container::before {
    content: "\\e908";
    font-family: "primeicons";
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    font-size: 1rem;
    color: var(--brand-gray-400);
    pointer-events: none;
  }

  .p-autocomplete-multiple-container:not(.p-disabled):hover {
    border-color: color-mix(
      in srgb,
      var(--brand-purple-500) 55%,
      var(--brand-white)
    );
  }

  .p-autocomplete-multiple-container:not(.p-disabled).p-focus {
    border-color: var(--brand-purple-500);
    box-shadow: 0 0 0 3px
      color-mix(in srgb, var(--brand-purple-500) 20%, transparent);
  }

  .p-autocomplete-input-token input {
    font-size: 0.875rem;
    color: var(--brand-black);
  }

  .p-autocomplete-input-token input::placeholder {
    color: var(--brand-gray-400);
  }
`;

export const StyledNoServices = styled.div`
  border: 1px dashed var(--brand-gray-200);
  border-radius: 12px;
`;

export const StyledSuggestionRow = styled.div`
  height: ${SUGGESTION_ITEM_HEIGHT}px;
  margin: -0.75rem -1.25rem;
  padding: 0 1rem;

  display: flex;
  align-items: center;
  gap: 0.75rem;

  &:hover,
  .p-autocomplete-item.p-highlight & {
    background: var(--brand-blue-25);
  }
`;

export const StyledServicesTable = styled.div`
  border: 1px solid var(--brand-gray-200);
  border-radius: 12px;
  overflow: hidden;

  .p-datatable-thead > tr > th {
    padding: 0.75rem 1rem;
    border: none;
    border-bottom: 1px solid var(--brand-gray-200);
    background: var(--brand-gray-50);

    font-size: 0.75rem;
    font-weight: var(--font-weight-semibold);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--brand-gray-600);
  }

  .p-datatable-tbody > tr > td {
    padding: 0.5rem 1rem;
  }

  .p-datatable-tbody > tr.p-datatable-row-expansion > td {
    padding: 0;
  }

  .p-datatable-tbody > tr:last-child > td {
    border-bottom: none;
  }
`;

export const StyledDropdown = styled(Dropdown)`
  &.p-dropdown {
    border-radius: 10px;
    background: var(--brand-gray-50);
    border: 1px solid var(--brand-gray-200);
  }

  &.p-dropdown:not(.p-disabled):hover {
    border-color: var(--brand-gray-400);
  }

  &.p-dropdown:not(.p-disabled).p-focus {
    border-color: var(--brand-purple-500);
    box-shadow: 0 0 0 3px
      color-mix(in srgb, var(--brand-purple-500) 20%, transparent);
  }

  .p-dropdown-label {
    font-size: 0.875rem;
    color: var(--brand-black);
  }

  .p-dropdown-trigger {
    color: var(--brand-purple-700);
  }
`;

export const StyledNumberInput = styled(InputNumber)`
  width: 100%;

  .p-inputnumber-input {
    width: 100%;
    border-radius: 10px;
    background: var(--brand-gray-50);
    border: 1px solid var(--brand-gray-200);
    font-size: 0.875rem;
    color: var(--brand-black);
  }

  .p-inputnumber-input:enabled:hover {
    border-color: var(--brand-gray-400);
  }

  .p-inputnumber-input:enabled:focus {
    border-color: var(--brand-purple-500);
    box-shadow: 0 0 0 3px
      color-mix(in srgb, var(--brand-purple-500) 20%, transparent);
  }
`;

export const StyledAmountField = styled.div<{ $needsReview: boolean }>`
  position: relative;
  width: 100%;

  ${({ $needsReview }) =>
    $needsReview &&
    `
    .p-inputnumber-input {
      padding-right: 2.25rem;
      border-color: var(--red-500);
      background: var(--brand-white);
    }

    .p-inputnumber-input:enabled:hover,
    .p-inputnumber-input:enabled:focus {
      border-color: var(--red-500);
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--red-500) 20%, transparent);
    }
  `}

  .amount-alert {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--red-500);
    cursor: help;
  }
`;

export const StyledAmountTooltip = styled(Tooltip)`
  &.p-tooltip .p-tooltip-text {
    padding: 0.5rem 0.875rem;
    border-radius: 999px;
    background: var(--brand-white);
    border: 1px solid var(--red-500);
    box-shadow: 0 2px 8px color-mix(in srgb, var(--red-500) 20%, transparent);

    font-size: 0.875rem;
    font-weight: var(--font-weight-semibold);
    color: var(--red-600);
  }

  &.p-tooltip .p-tooltip-arrow {
    display: none;
  }
`;

export const StyledBundlePanel = styled.div`
  background: color-mix(in srgb, var(--brand-gray-50) 40%, var(--brand-white));
`;

export const StyledBundleRow = styled.div`
  border: 1px solid var(--brand-gray-200);
  border-radius: 12px;
  background: var(--brand-gray-50);

  .p-button.p-button-text {
    width: 2rem;
    height: 2rem;
    color: var(--brand-purple-700);
  }

  .p-button.p-button-text:enabled:hover {
    color: var(--brand-purple-700);
  }

  .p-button.p-button-text .p-button-icon {
    font-size: 0.9375rem;
  }
`;

export const StyledTrashButton = styled(Button)`
  &.p-button {
    width: 2.25rem;
    height: 2.25rem;
    color: var(--brand-gray-600);
  }

  &.p-button .p-button-icon {
    font-size: 1.125rem;
  }
`;

export const StyledBreakdownPanel = styled.div`
  margin-top: 1rem;
  border: 1px solid var(--brand-gray-200);
  border-radius: 12px;
  overflow: hidden;
  background: var(--brand-white);

  .breakdown-header {
    width: 100%;
    padding: 1rem 1.25rem;
    border: none;
    background: var(--brand-white);
    cursor: pointer;

    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .breakdown-header:hover {
    background: color-mix(
      in srgb,
      var(--brand-purple-700) 8%,
      var(--brand-white)
    );
  }

  .breakdown-body {
    border-top: 1px solid var(--brand-gray-200);
  }

  /* Neither breakdown table has column headers, so PrimeReact's empty
     header row would render as a blank strip. */
  .p-datatable-thead {
    display: none;
  }

  .p-datatable-tbody > tr > td {
    padding: 0.875rem 1.25rem;
    border: none;
    border-bottom: 1px solid var(--brand-gray-200);
  }

  .p-datatable-tbody > tr:last-child > td {
    border-bottom: none;
  }
`;
